import { useState } from 'react'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import { Badge } from '@/common/ui/badge'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Button } from '@/common/ui/button'
import { Skeleton } from '@/common/ui/skeleton'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import useQueryGetMessageHistory from '../../api/hooks/useQueryGetMessageHistory'
import { getConditionLabel, getChannelLabel } from '../../utils/conditionLabels'
import {
	MessageChannel,
	MessageTransactionType,
	LineTransactionType,
} from '../../types'

import type { MessageTransactionModel } from '../../types'

const PAGE_SIZE = 10

const MessageCampaignsPage = () => {
	const navigate = useNavigate()
	const [page, setPage] = useState(0)

	const { data, isLoading } = useQueryGetMessageHistory({
		skip: page * PAGE_SIZE,
		take: PAGE_SIZE,
	})

	const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0

	const getTransactionTypeBadge = (type: MessageTransactionType) => {
		switch (type) {
			case MessageTransactionType.IMMEDIATE:
				return <Badge variant="default">ส่งทันที</Badge>
			case MessageTransactionType.SCHEDULED:
				return <Badge variant="secondary">กำหนดเวลา</Badge>
			case MessageTransactionType.AUTO:
				return <Badge variant="outline">อัตโนมัติ</Badge>
			default:
				return <Badge>{type}</Badge>
		}
	}

	const getChannelBadges = (campaign: MessageTransactionModel) => {
		const channels: JSX.Element[] = []

		if (campaign.smsTransaction) {
			channels.push(
				<Badge key="sms" variant="default">
					{getChannelLabel(MessageChannel.SMS)}
				</Badge>,
			)
		}

		if (campaign.lineTransaction) {
			// Check if it's a flex hot game, promotion, or regular LINE message
			if (campaign.lineTransactionType === LineTransactionType.FLEX_HOT_GAME) {
				channels.push(
					<Badge
						key="line-hot-game"
						variant="default"
						className="bg-purple-600"
					>
						{getChannelLabel(MessageChannel.LINE_HOT_GAME)}
					</Badge>,
				)
			} else if (
				campaign.lineTransactionType === LineTransactionType.FLEX_PROMOTION
			) {
				channels.push(
					<Badge key="line-promotion" variant="default" className="bg-pink-600">
						{getChannelLabel(MessageChannel.LINE_PROMOTION)}
					</Badge>,
				)
			} else {
				channels.push(
					<Badge key="line" variant="default" className="bg-green-600">
						{getChannelLabel(MessageChannel.LINE)}
					</Badge>,
				)
			}
		}

		return channels.length > 0 ? (
			channels
		) : (
			<span className="text-muted-foreground">-</span>
		)
	}

	return (
		<PageContainer>
			<div className="space-y-4 px-4 sm:px-0">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
					<Heading
						title="รายการส่งข้อความ"
						description="รายการข้อความที่ส่งทั้งหมด"
					/>
					<Button onClick={() => navigate('/operator/messaging/create')}>
						<Plus className="mr-2 h-4 w-4" />
						ส่งข้อความ
					</Button>
				</div>

				<Separator className="my-4 sm:my-6" />

				{isLoading ? (
					<div className="space-y-3">
						{[...Array(5)].map((_, i) => (
							<Skeleton key={i} className="h-12 w-full" />
						))}
					</div>
				) : (
					<>
						<div className="rounded-md border">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>ข้อความ</TableHead>
										<TableHead>ช่องทาง</TableHead>
										<TableHead>เงื่อนไข</TableHead>
										<TableHead>ประเภท</TableHead>
										<TableHead>วันที่สร้าง</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data?.data && data.data.length > 0 ? (
										data.data.map((campaign) => (
											<TableRow key={campaign.id}>
												<TableCell className="max-w-xs">
													<div className="truncate" title={campaign.message}>
														{campaign.message}
													</div>
												</TableCell>
												<TableCell>
													<div className="flex gap-2">
														{getChannelBadges(campaign)}
													</div>
												</TableCell>
												<TableCell>
													{getConditionLabel(campaign.messageGroupCondition)}
												</TableCell>
												<TableCell>
													{getTransactionTypeBadge(
														campaign.messageTransactionType,
													)}
												</TableCell>
												<TableCell>
													{format(
														new Date(campaign.createdAt),
														'dd MMM yyyy HH:mm',
														{ locale: th },
													)}
												</TableCell>
											</TableRow>
										))
									) : (
										<TableRow>
											<TableCell colSpan={5} className="text-center">
												ไม่พบข้อมูล
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</div>

						{/* Pagination */}
						{totalPages > 1 && (
							<div className="mt-4 flex items-center justify-between">
								<div className="text-sm text-muted-foreground">
									หน้า {page + 1} จาก {totalPages}
								</div>
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										onClick={() => setPage((p) => Math.max(0, p - 1))}
										disabled={page === 0}
									>
										<ChevronLeft className="h-4 w-4" />
										ก่อนหน้า
									</Button>
									<Button
										variant="outline"
										size="sm"
										onClick={() => setPage((p) => p + 1)}
										disabled={page >= totalPages - 1}
									>
										ถัดไป
										<ChevronRight className="h-4 w-4" />
									</Button>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</PageContainer>
	)
}

const GuardedMessageCampaignsPage = withUserPermission(MessageCampaignsPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_SMS],
})

export { GuardedMessageCampaignsPage as MessageCampaignsPage }
